/**
 * mail-merge.ts
 *
 * Utility functions for merging template tokens with prospect/ae context,
 * building action links (mailto, tel, sms), and clipboard helpers.
 *
 * Token format: {token_name} in templates.
 * Missing tokens render as [TOKEN_NAME] (uppercase) so aes spot them immediately.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MergeContext {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  title?: string;
  company?: string;
  rep_name?: string;
  rep_signature?: string;
  trigger_event?: string;
  pain_hint?: string;
  // Allow arbitrary extension
  [key: string]: string | undefined;
}

// ─── Core merge function ──────────────────────────────────────────────────────

/**
 * Replaces {token_name} placeholders with values from context.
 * Missing tokens render as [TOKEN_NAME] so aes immediately spot gaps.
 */
export function mergeTemplate(template: string, context: MergeContext): string {
  return template.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_match, key: string) => {
    const value = context[key];
    if (value !== undefined && value !== "") {
      return value;
    }
    // Render missing token in uppercase bracket notation
    return `[${key.toUpperCase()}]`;
  });
}

// ─── Rep context ─────────────────────────────────────────────────────────────

/**
 * Maps internal role keys to display labels used in email signatures.
 */
const ROLE_DISPLAY: Record<string, string> = {
  ae: "AE",
  bdr: "BDR",
  manager: "Manager",
  executive: "Executive",
  marketing: "Marketing",
  se: "SE",
};

/**
 * Picks the company label shown under the user's name in the signature.
 * Mi6 Group staff sign as Mi6; everyone else signs as their tenant company
 * (Attilius for tenant 1). Outlook / external addresses still belong
 * to the tenant on tenant 1.
 */
function companyLabelFor(email: string, tenantName?: string): string {
  const lower = (email || "").toLowerCase();
  if (lower.endsWith("@mi6group.com")) return "Mi6 Group";
  return tenantName || "Attilius";
}

export interface RepLike {
  name?: string | null;
  email?: string | null;
  role?: string | null;
  tenantName?: string | null;
}

/**
 * Builds the rep merge context from the live session user. Every user gets
 * their own real name, role label, and email in the signature — no more
 * hardcoded Demo BDR / @mi6group.com defaults bleeding into other
 * users' templates.
 *
 * Pass `null`/`undefined` only as a last-resort fallback for unauthenticated
 * preview screens; live app code paths must always pass the session user.
 */
export function getDefaultRepContext(user?: RepLike | null): MergeContext {
  if (!user || !user.email) {
    // Generic placeholder for preview / public demo screens only.
    return {
      rep_name: "Your Name",
      rep_signature: "Your Name\nAttilius\nyour.email@attilius.com",
    };
  }
  const name = (user.name || user.email.split("@")[0]).trim();
  // Split rep name into first / last so scripts that use {rep_first_name}
  // ("Hi {first_name}, this is {rep_first_name} from Mi6 Group...") render
  // cleanly instead of showing [REP_FIRST_NAME] in red.
  const repParts = name.split(/\s+/);
  const repFirstName = repParts[0] || name;
  const repLastName = repParts.slice(1).join(" ");
  const roleKey = (user.role || "").toLowerCase();
  const roleLabel = ROLE_DISPLAY[roleKey] || "";
  const company = companyLabelFor(user.email, user.tenantName ?? undefined);
  const orgLine = roleLabel ? `${company} · ${roleLabel}` : company;
  return {
    rep_name: name,
    rep_first_name: repFirstName,
    rep_last_name: repLastName,
    rep_email: user.email,
    rep_company: company,
    rep_signature: `${name}\n${orgLine}\n${user.email}`,
  };
}

// ─── Prospect context (D-071: senior-buyer salutation) ───────────────────────

/**
 * Title-pattern set we treat as "senior buyer" — until the rep tells us
 * otherwise we address these people formally (Mr./Ms. {last_name}) rather
 * than by first name. Order-independent, case-insensitive substring/regex match.
 *
 * Includes: Chief, C-suite (C[A-Z]O), President, SVP, EVP, VP / Vice President,
 * Managing Partner / Director, Head of, General Manager, Owner, Founder.
 */
const SENIOR_TITLE_PATTERNS: RegExp[] = [
  /\bchief\b/i,
  /\bc[a-z]o\b/i,                // CEO, CFO, COO, CTO, CRO, CIO, etc.
  /\bpresident\b/i,
  /\bsvp\b/i,
  /\bevp\b/i,
  /\bvp\b/i,
  /\bvice\s+president\b/i,
  /\bmanaging\s+(?:partner|director)\b/i,
  /\bhead\s+of\b/i,
  /\bgeneral\s+manager\b/i,
  /\bowner\b/i,
  /\bfounder\b/i,
  /\bco-?founder\b/i,
  /\bprincipal\b/i,
  /\bpartner\b/i,
];

/**
 * True when the title appears to be senior enough to warrant formal address.
 * Empty/missing titles return false (we default to first-name address for
 * unknown seniority — same behavior as today).
 */
export function isSeniorTitle(title?: string | null): boolean {
  if (!title) return false;
  return SENIOR_TITLE_PATTERNS.some((pat) => pat.test(title));
}

/**
 * High-confidence first-name → gender lookup. Used only to pick Mr./Ms. for
 * senior contacts where we want to address them formally. Unknown names
 * fall back to no-honorific formal address (full name) — never guess Mr.
 * when we don't actually know.
 *
 * Source: ~top 200 US male / female names by frequency. Deliberately
 * conservative — common ambiguous names (Alex, Jordan, Taylor, Morgan,
 * Pat, Sam, Casey, Riley, Cameron, Jamie, Avery, Quinn, Rowan, etc.) are
 * NOT included so we don't misgender.
 */
const MALE_FIRST_NAMES = new Set<string>([
  "james", "john", "robert", "michael", "william", "david", "richard", "joseph",
  "thomas", "charles", "christopher", "daniel", "matthew", "anthony", "mark",
  "donald", "steven", "paul", "andrew", "joshua", "kenneth", "kevin", "brian",
  "george", "edward", "ronald", "timothy", "jason", "jeffrey", "ryan", "jacob",
  "gary", "nicholas", "eric", "jonathan", "stephen", "larry", "justin", "scott",
  "brandon", "benjamin", "frank", "gregory", "raymond", "samuel", "patrick",
  "alexander", "jack", "dennis", "jerry", "tyler", "aaron", "henry", "douglas",
  "peter", "adam", "nathan", "zachary", "walter", "kyle", "harold", "carl",
  "jeremy", "keith", "roger", "gerald", "ethan", "arthur", "terry", "christian",
  "sean", "lawrence", "austin", "joe", "noah", "jesse", "albert", "bryan",
  "billy", "bruce", "willie", "jordan", "dylan", "alan", "ralph", "gabriel",
  "roy", "juan", "wayne", "eugene", "logan", "randy", "louis", "russell",
  "vincent", "philip", "bobby", "johnny", "bradley", "martin", "jesus",
  "howard", "victor", "phillip", "todd", "craig", "alfred", "shawn", "clarence",
  "sean", "earl", "chris", "tony", "dale", "derek", "warren", "darrell",
  "glenn", "jeff", "bob", "mike", "jim", "steve", "tim", "dave", "rich",
  "tom", "dan", "don", "ron", "ed", "al", "max", "max", "luke", "oscar",
  "miguel", "carlos", "jose", "luis", "marcus", "derrick", "travis", "jared",
  "trevor", "colin", "ian", "liam", "mason", "owen", "hunter", "connor",
  "caleb", "isaac", "levi", "wyatt", "andre", "erik", "corey", "omar",
  "lance", "clayton", "shane", "darren", "karl", "marvin", "perry", "ricky",
  "barry", "leonard", "theodore", "clifford", "stanley", "leon", "manuel",
  "reginald", "rodney", "micheal", "calvin", "chad", "melvin", "floyd",
  "sergio", "hector", "javier", "raul", "eduardo", "alberto", "ricardo",
  "fernando", "jorge", "diego", "pedro", "rafael", "oscar", "andres",
  "jeff", "kemal", "shobhit", "mahesh", "sreejit", "michael",
  "bryan",
]);

const FEMALE_FIRST_NAMES = new Set<string>([
  "mary", "patricia", "jennifer", "linda", "elizabeth", "barbara", "susan",
  "jessica", "sarah", "karen", "nancy", "lisa", "betty", "margaret", "sandra",
  "ashley", "kimberly", "emily", "donna", "michelle", "carol", "amanda",
  "melissa", "deborah", "stephanie", "rebecca", "laura", "sharon", "cynthia",
  "kathleen", "amy", "shirley", "angela", "helen", "anna", "brenda", "pamela",
  "nicole", "emma", "samantha", "katherine", "christine", "debra", "rachel",
  "catherine", "carolyn", "janet", "ruth", "maria", "heather", "diane",
  "virginia", "julie", "joyce", "victoria", "olivia", "kelly", "christina",
  "lauren", "joan", "evelyn", "judith", "andrea", "hannah", "megan", "cheryl",
  "jacqueline", "martha", "madison", "teresa", "gloria", "sara", "janice",
  "ann", "kathryn", "abigail", "sophia", "frances", "jean", "alice", "judy",
  "isabella", "julia", "grace", "amber", "denise", "danielle", "marilyn",
  "beverly", "charlotte", "natalie", "theresa", "diana", "brittany", "doris",
  "kayla", "alexis", "lori", "marie", "tiffany", "michele", "jamie", "erin",
  "chelsea", "melanie", "crystal", "holly", "monica", "rose", "vanessa",
  "stacey", "colleen", "yvonne", "jenna", "misty", "yolanda", "becky",
  "audrey", "sherri", "darlene", "naomi", "sheila", "alicia", "tina",
  "erica", "tracy", "wendy", "valerie", "gail", "connie", "daisy", "sonia",
  "sonya", "jasmine", "leah", "sonja", "becky", "tara", "krista", "kristen",
  "kristin", "kristina", "meghan", "meg", "maggie", "molly", "caitlin",
  "shannon", "sherry", "renee", "kayla", "haley", "savannah", "ella", "chloe",
  "lily", "zoey", "layla", "aubrey", "adriana", "angelica", "esther",
  "adriana", "camila", "sofia", "valentina", "isabel", "carmen", "rosa",
  "elena", "gabriela", "daniela", "natalia", "alejandra", "laura", "adriana",
  "priya", "divya", "anjali", "aisha", "fatima",
]);

export type GuessedGender = "m" | "f" | "unknown";

/**
 * Returns 'm', 'f', or 'unknown' for a first name. Conservative: unknown
 * for any name not on the high-confidence list, and unknown for ambiguous
 * unisex names (Alex, Jordan, Taylor, Morgan, etc. are deliberately omitted
 * from both lists).
 */
export function guessGender(firstName?: string | null): GuessedGender {
  if (!firstName) return "unknown";
  const key = firstName.trim().toLowerCase().split(/[\s\-']/)[0];
  if (!key) return "unknown";
  if (MALE_FIRST_NAMES.has(key)) return "m";
  if (FEMALE_FIRST_NAMES.has(key)) return "f";
  return "unknown";
}

export interface ProspectLike {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  title?: string | null;
  company?: string | null;
  email?: string | null;
}

/**
 * Builds the salutation tokens for a prospect, choosing formal vs first-name
 * address based on title seniority.
 *
 * Returns three tokens:
 *  - {salutation}      → "Mr." / "Ms." / "" (empty when gender unknown or junior)
 *  - {formal_address}  → "Mr. Smith" / "Ms. Smith" / "{first_name} {last_name}"
 *                        when senior but gender unknown / "{first_name}" otherwise
 *  - {greeting_name}   → the recommended drop-in greeting form. Senior+known →
 *                        "Mr./Ms. Lastname"; senior+unknown → full name;
 *                        junior → first name. This is what cadence scripts
 *                        should use in their openers ("Hi {greeting_name},").
 */
export function buildProspectSalutation(prospect: ProspectLike): MergeContext {
  const firstName = (prospect.first_name ?? "").trim();
  const lastName = (prospect.last_name ?? "").trim();
  const fullName = (prospect.full_name ?? "").trim();

  // Derive first / last from full_name if explicit fields are missing.
  const parts = fullName.split(/\s+/).filter(Boolean);
  const fn = firstName || parts[0] || "";
  const ln = lastName || (parts.length > 1 ? parts.slice(1).join(" ") : "");

  const senior = isSeniorTitle(prospect.title);
  const gender = guessGender(fn);

  let salutation = "";
  let formalAddress = "";
  let greetingName = fn || fullName || "";

  if (senior) {
    if (gender === "m") {
      salutation = "Mr.";
      formalAddress = ln ? `Mr. ${ln}` : (fn ? `Mr. ${fn}` : "");
      greetingName = formalAddress || greetingName;
    } else if (gender === "f") {
      salutation = "Ms.";
      formalAddress = ln ? `Ms. ${ln}` : (fn ? `Ms. ${fn}` : "");
      greetingName = formalAddress || greetingName;
    } else {
      // Senior, unknown gender — use full name formally, no honorific guess.
      salutation = "";
      formalAddress = fullName || (fn && ln ? `${fn} ${ln}` : fn);
      greetingName = formalAddress || greetingName;
    }
  }

  return {
    salutation,
    formal_address: formalAddress,
    greeting_name: greetingName,
  };
}

// ─── Link builders ────────────────────────────────────────────────────────────

/**
 * Builds a mailto: URI with subject and body properly URI-encoded.
 */
export function buildMailto(to: string, subject: string, body: string): string {
  const params = new URLSearchParams();
  // URLSearchParams uses + for spaces; mailto requires %20
  const encode = (s: string) =>
    encodeURIComponent(s).replace(/\+/g, "%20");

  return `mailto:${encodeURIComponent(to)}?subject=${encode(subject)}&body=${encode(body)}`;
}

/**
 * Normalizes a phone number to a tel: URI.
 * Strips spaces, dashes, parentheses — keeps leading +.
 */
export function buildTel(phone: string): string {
  const normalized = phone.replace(/[\s\-().]/g, "");
  return `tel:${normalized}`;
}

/**
 * Builds an sms: URI.
 */
export function buildSms(phone: string, body?: string): string {
  const normalized = phone.replace(/[\s\-().]/g, "");
  if (body) {
    return `sms:${normalized}?body=${encodeURIComponent(body)}`;
  }
  return `sms:${normalized}`;
}

// ─── Clipboard ────────────────────────────────────────────────────────────────

/**
 * Copies text to clipboard.
 * Uses navigator.clipboard.writeText() with a textarea fallback for
 * environments where the Clipboard API isn't available.
 */
export async function copyToClipboard(text: string): Promise<void> {
  // Primary: Clipboard API (requires HTTPS or localhost)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to legacy approach
    }
  }

  // Fallback: execCommand (deprecated but widely supported)
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  textarea.setAttribute("readonly", "");
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
}

// ─── Token extraction helper ─────────────────────────────────────────────────

/**
 * Returns an array of token names found in a template string (deduplicated).
 */
export function extractTokens(template: string): string[] {
  const matches = template.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g) ?? [];
  const names = matches.map((m) => m.slice(1, -1));
  return [...new Set(names)];
}
